import { useCallback, useEffect, useState } from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { getApiError } from "../api/errors";
import { useTranslation } from "react-i18next";
import { getPricingPolicy, updatePricingPolicy } from "../api/pricing";
import { LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { useAIPageContext } from "../context/AIAssistantContext";

const ROUNDING_OPTIONS = ["0.01", "0.05", "0.10", "0.25", "0.50", "1.00"];

function toForm(policy) {
  return {
    currency: policy?.currency || "USD",
    tax_percent: String(policy?.tax_percent ?? 0),
    max_discount_percent: String(policy?.max_discount_percent ?? 10),
    auto_approve_discount_percent: String(policy?.auto_approve_discount_percent ?? 0),
    minimum_margin_percent: Number(policy?.minimum_margin_percent ?? 0) === 0
      ? "" : String(policy.minimum_margin_percent),
    owner_max_discount_percent: String(policy?.owner_max_discount_percent ?? 10),
    admin_max_discount_percent: String(policy?.admin_max_discount_percent ?? 10),
    staff_max_discount_percent: String(policy?.staff_max_discount_percent ?? 5),
    price_rounding_increment: String(policy?.price_rounding_increment ?? "0.01"),
    require_cost_for_discount: Boolean(policy?.require_cost_for_discount),
    default_validity_days: String(policy?.default_validity_days ?? 14),
    default_terms: policy?.default_terms || "",
    require_customer_for_quotation: Boolean(policy?.require_customer_for_quotation),
    allow_self_approval: Boolean(policy?.allow_self_approval),
  };
}

function parsePercent(value, label, { upper = 100, t } = {}) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0 || number > upper) {
    throw new Error(t ? t("pricing.percentRange", { label, upper }) : `${label} must be between 0 and ${upper}.`);
  }
  return number;
}

export default function PricingPolicy() {
  const { t } = useTranslation();
  const { canManage, selectedWorkspace } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [form, setForm] = useState(toForm(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  useAIPageContext({ version: 1, page_id: "pricing", section: advancedOpen ? "advanced" : "policy" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getPricingPolicy();
      setPolicy(response.data);
      setForm(toForm(response.data));
    } catch (requestError) {
      setError(getApiError(requestError, t("pricing.loadError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, selectedWorkspace?.public_id]);

  const save = async () => {
    setError("");
    setSuccess("");

    let payload;
    try {
      const currency = form.currency.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(currency)) {
        throw new Error(t("pricing.currencyInvalid"));
      }

      const hardMaximum = parsePercent(form.max_discount_percent, t("pricing.hardMaximum"), { t });
      const ownerMaximum = parsePercent(form.owner_max_discount_percent, t("pricing.ownerDiscount"), { t });
      const adminMaximum = parsePercent(form.admin_max_discount_percent, t("pricing.adminDiscount"), { t });
      const staffMaximum = parsePercent(form.staff_max_discount_percent, t("pricing.staffDiscount"), { t });
      const reviewThreshold = parsePercent(
        form.auto_approve_discount_percent,
        t("pricing.reviewThreshold"),
        { t },
      );
      const minimumMargin = form.minimum_margin_percent.trim() === ""
        ? 0
        : parsePercent(form.minimum_margin_percent, t("pricing.minimumMargin"), { upper: 99.99, t });

      if (ownerMaximum > hardMaximum) {
        throw new Error(t("pricing.ownerExceeds"));
      }
      if (adminMaximum > ownerMaximum) {
        throw new Error(t("pricing.adminExceeds"));
      }
      if (staffMaximum > adminMaximum) {
        throw new Error(t("pricing.staffExceeds"));
      }
      if (reviewThreshold > hardMaximum) {
        throw new Error(t("pricing.reviewExceeds"));
      }
      if (!ROUNDING_OPTIONS.includes(form.price_rounding_increment)) {
        throw new Error(t("pricing.roundingInvalid"));
      }

      const validityDays = Number(form.default_validity_days);
      if (!Number.isInteger(validityDays) || validityDays < 1 || validityDays > 365) {
        throw new Error(t("pricing.validityInvalid"));
      }

      payload = {
        expected_version: policy?.version || 1,
        currency,
        tax_percent: parsePercent(form.tax_percent, t("pricing.taxLabel"), { t }),
        max_discount_percent: hardMaximum,
        auto_approve_discount_percent: reviewThreshold,
        minimum_margin_percent: minimumMargin,
        owner_max_discount_percent: ownerMaximum,
        admin_max_discount_percent: adminMaximum,
        staff_max_discount_percent: staffMaximum,
        require_cost_for_discount: form.require_cost_for_discount,
        price_rounding_increment: Number(form.price_rounding_increment),
        default_validity_days: validityDays,
        default_terms: form.default_terms.trim() || null,
        require_customer_for_quotation: form.require_customer_for_quotation,
        allow_self_approval: form.allow_self_approval,
      };
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    setSaving(true);
    try {
      const response = await updatePricingPolicy(payload);
      setPolicy(response.data);
      setForm(toForm(response.data));
      setSuccess(t("pricing.saved"));
    } catch (requestError) {
      setError(getApiError(requestError, t("pricing.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  return (
    <MainLayout>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={900} letterSpacing={-0.7}>
            {t("nav.pricing")}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {t("pricing.subtitle")}
          </Typography>
        </Box>
        <Chip
          label={t(canManage ? "pricing.management" : "pricing.readOnly")}
          color={canManage ? "primary" : "default"}
          sx={{ alignSelf: { xs: "flex-start", md: "center" }, fontWeight: 800 }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}
      {!canManage && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          {t("pricing.readOnlyHelp")}
        </Alert>
      )}

      {loading ? (
        <LoadingPanel label={t("pricing.loading")} />
      ) : (
        <>
          <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 14px 35px rgba(15,23,42,.05)" }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
              <Typography variant="h6" fontWeight={900} sx={{ mb: 0.5 }}>{t("pricing.everyday")}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {t("pricing.everydayHelp")}
              </Typography>
              <Grid container spacing={2.25}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth label={t("pricing.currency")} inputProps={{ maxLength: 3 }} value={form.currency} disabled={!canManage} onChange={(event) => setField("currency", event.target.value.toUpperCase())} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth type="number" label={t("pricing.tax")} inputProps={{ min: 0, max: 100, step: 0.01 }} value={form.tax_percent} disabled={!canManage} onChange={(event) => setField("tax_percent", event.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth type="number" label={t("pricing.margin")} helperText={t("pricing.marginHelp")} inputProps={{ min: 0, max: 99.99, step: 0.01 }} value={form.minimum_margin_percent} disabled={!canManage} onChange={(event) => setField("minimum_margin_percent", event.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth type="number" label={t("pricing.maximum")} helperText={t("pricing.maximumHelp")} inputProps={{ min: 0, max: 100, step: 0.01 }} value={form.max_discount_percent} disabled={!canManage} onChange={(event) => setField("max_discount_percent", event.target.value)} />
                </Grid>
              </Grid>

              <Accordion expanded={advancedOpen} onChange={(_, expanded) => setAdvancedOpen(expanded)} disableGutters sx={{ mt: 3, boxShadow: "none", border: "1px solid", borderColor: "divider", borderRadius: "12px !important", "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} aria-controls="advanced-pricing-settings" id="advanced-pricing-heading">
                  <Typography fontWeight={800}>{t("pricing.advanced")}</Typography>
                </AccordionSummary>
                <AccordionDetails id="advanced-pricing-settings" sx={{ pt: 1, pb: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1.5 }}>{t("pricing.permissions")}</Typography>
                  <Grid container spacing={2.25}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField select fullWidth label={t("pricing.rounding")} value={form.price_rounding_increment} disabled={!canManage} onChange={(event) => setField("price_rounding_increment", event.target.value)}>
                        {ROUNDING_OPTIONS.map((value) => <MenuItem key={value} value={value}>${value}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth type="number" label={t("pricing.ownerLimit")} inputProps={{ min: 0, max: 100, step: 0.01 }} value={form.owner_max_discount_percent} disabled={!canManage} onChange={(event) => setField("owner_max_discount_percent", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth type="number" label={t("pricing.adminLimit")} inputProps={{ min: 0, max: 100, step: 0.01 }} value={form.admin_max_discount_percent} disabled={!canManage} onChange={(event) => setField("admin_max_discount_percent", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth type="number" label={t("pricing.staffLimit")} inputProps={{ min: 0, max: 100, step: 0.01 }} value={form.staff_max_discount_percent} disabled={!canManage} onChange={(event) => setField("staff_max_discount_percent", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField fullWidth type="number" label={t("pricing.threshold")} helperText={t("pricing.thresholdHelp")} inputProps={{ min: 0, max: 100, step: 0.01 }} value={form.auto_approve_discount_percent} disabled={!canManage} onChange={(event) => setField("auto_approve_discount_percent", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <FormControlLabel control={<Switch checked={form.require_cost_for_discount} disabled={!canManage} onChange={(event) => setField("require_cost_for_discount", event.target.checked)} />} label={t("pricing.requireCost")} />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                        {t("pricing.costHelp")}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" fontWeight={900} sx={{ mt: 3, mb: 1.5 }}>{t("pricing.quotation")}</Typography>
                  <Grid container spacing={2.25}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="number" label={t("pricing.validity")} inputProps={{ min: 1, max: 365, step: 1 }} value={form.default_validity_days} disabled={!canManage} onChange={(event) => setField("default_validity_days", event.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline minRows={4} label={t("pricing.terms")} value={form.default_terms} disabled={!canManage} onChange={(event) => setField("default_terms", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel control={<Switch checked={form.require_customer_for_quotation} disabled={!canManage} onChange={(event) => setField("require_customer_for_quotation", event.target.checked)} />} label={t("pricing.requireCustomer")} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel control={<Switch checked={form.allow_self_approval} disabled={!canManage} onChange={(event) => setField("allow_self_approval", event.target.checked)} />} label={t("pricing.selfApproval")} />
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>{t("pricing.version", { version: policy?.version || 1 })}</Typography>
                </AccordionDetails>
              </Accordion>

              {canManage && (
                <Stack direction="row" justifyContent="flex-end" spacing={1.25} sx={{ mt: 3 }}>
                  <Button variant="outlined" onClick={load} disabled={saving}>{t("pricing.reset")}</Button>
                  <Button variant="contained" onClick={save} disabled={saving}>{t(saving ? "pricing.saving" : "pricing.save")}</Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </MainLayout>
  );
}
